<template>
    <div v-if="isDisabled" class="phu-slot phu-slot--disabled" aria-disabled="true">
        <i class="i-andy-close i-andy-xl" aria-hidden="true"></i>
    </div>

    <button
        v-else
        type="button"
        class="phu-slot"
        :class="slotClasses"
        @pointerdown="onPress"
        @contextmenu.prevent
    >
        <span class="phu-slot__surface">
            <span class="phu-slot__name">{{ productName }}</span>
            <span v-if="stateLabel" class="phu-slot__state">{{ stateLabel }}</span>
            <span v-if="detail" class="phu-slot__detail">
                <transition name="phu-slot-detail">
                    <span :key="detailMode" class="phu-slot__detail-value">{{ detail }}</span>
                </transition>
            </span>

            <ProgressBar v-if="progressVariant" class="phu-slot__progress" :items-completed="progressPercent" :items-total="100" :variant="progressVariant" />
        </span>
    </button>
</template>

<script>
import ProgressBar from '@/components/domain/ui/ProgressBar.vue'
import { SLOT_STATES } from '@/store/static/phuProduction'
import { formatCountdown, elapsedPercent, remainingPercent } from '../utils/time'
import { resolveSlotState } from '../utils/slotState'

const FULL_PERCENT = 100

const PROGRESS_BY_STATE = {
    [SLOT_STATES.COOKING]: 'cooking',
    [SLOT_STATES.READY]: 'success',
    [SLOT_STATES.ABOUT_TO_EXPIRE]: 'warning',
    [SLOT_STATES.EXPIRED]: 'error'
}

export default {
    name: 'PhuSlot',
    components: { ProgressBar },
    props: {
        slotItem: { type: Object, required: true },
        product: { type: Object, default: null },
        now: { type: Number, required: true },
        forceDisabled: { type: Boolean, default: false },
        selectable: { type: Boolean, default: false },
        selected: { type: Boolean, default: false },
        selectedUnits: { type: Number, default: 0 },
        showTime: { type: Boolean, default: false },
        draggable: { type: Boolean, default: false },
        holding: { type: Boolean, default: false },
        armed: { type: Boolean, default: false },
        dragging: { type: Boolean, default: false },
        dropZone: { type: Boolean, default: false },
        dropTarget: { type: Boolean, default: false }
    },
    computed: {
        slotState() {
            return resolveSlotState(this.slotItem, this.now)
        },
        isDisabled() {
            return this.forceDisabled || this.slotState === SLOT_STATES.DISABLED || !this.product
        },
        productName() {
            return this.product ? this.product.name : ''
        },
        slotClasses() {
            return {
                [`phu-slot--${this.slotState}`]: true,
                'phu-slot--selectable': this.selectable,
                'phu-slot--selected': this.selected,
                'phu-slot--draggable': this.draggable,
                'phu-slot--holding': this.holding,
                'phu-slot--armed': this.armed,
                'phu-slot--dragging': this.dragging,
                'phu-slot--drop': this.dropZone,
                'phu-slot--target': this.dropTarget,
                'phu-slot--time': this.showTime
            }
        },
        stateLabel() {
            if (this.slotState === SLOT_STATES.COOKING) {
                return this.$t('phu_production.slot.cooking')
            }
            if (this.slotState === SLOT_STATES.EXPIRED) {
                if (this.showTime && this.slotItem.expires_at) {
                    return this.$t('phu_production.slot.expired_ago', {
                        time: formatCountdown(this.now - this.slotItem.expires_at)
                    })
                }
                return this.$t('phu_production.slot.expired')
            }
            return ''
        },
        detailMode() {
            if (this.slotState === SLOT_STATES.EMPTY) {
                return 'units'
            }
            return this.showTime ? 'time' : 'units'
        },
        detail() {
            if (this.slotState === SLOT_STATES.EMPTY) {
                return this.selected ? String(this.selectedUnits) : ''
            }
            if (this.showTime) {
                return this.slotState === SLOT_STATES.EXPIRED ? '' : this.timeLeft
            }
            return String(this.slotItem.units)
        },
        timeLeft() {
            if (this.slotState === SLOT_STATES.COOKING && this.slotItem.ready_at) {
                return formatCountdown(this.slotItem.ready_at - this.now)
            }
            if (this.slotItem.expires_at) {
                return formatCountdown(this.slotItem.expires_at - this.now)
            }
            return String(this.slotItem.units)
        },
        progressVariant() {
            if (this.selected) {
                return 'success'
            }
            return PROGRESS_BY_STATE[this.slotState] || null
        },
        progressPercent() {
            const { started_at: startedAt, ready_at: readyAt, expires_at: expiresAt } = this.slotItem

            if (this.selected || this.slotState === SLOT_STATES.EXPIRED) {
                return FULL_PERCENT
            }
            if (this.slotState === SLOT_STATES.COOKING) {
                return elapsedPercent(startedAt, readyAt, this.now)
            }
            return remainingPercent(readyAt, expiresAt, this.now)
        }
    },
    methods: {
        onPress(event) {
            if (event.button > 0) {
                return
            }
            this.$emit('press', event)
        }
    }
}
</script>

<style lang="scss" scoped>
@import '~@/components/domain/phu-production/styles/tokens';

.phu-slot {
    box-sizing: border-box;
    @include display-flex();
    flex: 1 1 auto;
    width: 100%;
    min-height: $phu-slot-min-height;
    padding: 0;
    border: none;
    border-radius: $phu-radius-s;
    background: transparent;
    cursor: pointer;
    touch-action: none;
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;

    &__surface {
        position: relative;
        @include display-flex();
        @include flex-direction(column);
        @include align-items(center);
        @include justify-content(center);
        flex: 1 1 auto;
        width: 100%;
        height: auto;
        padding: 4px;
        border: 2px solid $neutro-dark;
        border-radius: $phu-radius-s;
        background-color: $phu-surface;
        text-align: center;
        box-sizing: border-box;
    }

    &__name {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        max-width: 100%;
        max-height: 40px;
        overflow: hidden;
        word-break: normal;
        overflow-wrap: normal;
        hyphens: none;
        font-family: $text-bold;
        font-size: 1rem;
        line-height: 20px;
        text-transform: uppercase;
        color: $phu-text;
        text-align: center;
    }

    &__state {
        font-family: $text-bold;
        font-size: 0.875rem;
        line-height: normal;
        color: $phu-warning;
    }

    &__detail {
        position: relative;
        display: block;
        min-height: 20px;
        font-family: $text;
        font-size: 0.875rem;
        line-height: 20px;
        color: $phu-text;
    }

    &__detail-value {
        display: block;
    }

    &__progress {
        position: absolute;
        right: 4px;
        bottom: 4px;
        left: 4px;
        width: auto;
    }

    ::v-deep .progress-bar-compact {
        background-color: $neutro-t30;
        @include border-radius(999px);
    }

    ::v-deep .progress-bar-compact__fill {
        @include border-radius(999px);
    }

    &--draggable {
        cursor: grab;
    }

    &--holding &__surface {
        box-shadow: inset 0 0 0 2px $phu-select;
    }

    &--armed {
        cursor: grabbing;
    }

    &--armed &__surface {
        box-shadow: inset 0 0 0 2px $phu-primary;
    }

    &--cooking#{&}--time &__detail-value {
        @include font-size(xs);
        color: $neutro-s50;
    }

    &--cooking &__progress ::v-deep .progress-bar-compact__fill {
        background-color: $phu-cooking-fill;
    }

    &--empty &__surface {
        border-width: 1px;
        border-style: dashed;
        border-color: $neutro-s30;
    }

    &--ready &__surface {
        border-color: $phu-success;
        background-color: $phu-available-bg;
    }

    &--ready &__progress ::v-deep .progress-bar-compact__fill {
        background-color: $phu-success;
    }

    &--about_to_expire &__surface {
        border-color: $phu-warning;
        background-color: $warning-t90;
    }

    &--about_to_expire &__progress ::v-deep .progress-bar-compact__fill {
        background-color: $phu-warning;
    }

    &--expired &__surface {
        border-color: $color-error-500;
        background-color: $error-t90;
    }

    &--expired &__state {
        color: $color-error-500;
    }

    &--expired &__progress ::v-deep .progress-bar-compact__fill {
        background-color: $color-error-500;
    }

    &--selected &__surface {
        border-width: 2px;
        border-style: solid;
        border-color: $phu-success;
        background-color: $phu-available-bg;
    }

    &--dragging {
        opacity: 0.35;
    }

    &--drop &__surface {
        border-width: 2px;
        border-style: solid;
        border-color: $phu-select;
        background-color: $phu-primary-soft;
    }

    &--target &__surface {
        border-color: $phu-select;
        background-color: $phu-primary-soft;
        box-shadow: inset 0 0 0 2px $phu-select;
    }

    .phu-slot-detail-enter-active,
    .phu-slot-detail-leave-active {
        transition: opacity 0.3s ease;
    }

    .phu-slot-detail-leave-active {
        position: absolute;
        right: 0;
        left: 0;
    }

    .phu-slot-detail-enter,
    .phu-slot-detail-leave-to {
        opacity: 0;
    }

    &--disabled {
        @include display-flex();
        @include align-items(center);
        @include justify-content(center);
        flex: 1 1 auto;
        width: 100%;
        min-height: $phu-slot-min-height;
        border: 1px solid $neutro-s30;
        background-color: $neutro-t80;
        color: $phu-disabled-icon;
        cursor: default;

        i{
            color: $neutro-dark;
        }
    }
}
</style>
